import {SeasonalThemeService} from '@services/seasonal-theme.service';
import {SeasonalSnowComponent} from '@/app/core/components/seasonal-snow/seasonal-snow.component';
import {Component, OnInit, ChangeDetectionStrategy, Inject, PLATFORM_ID, DestroyRef, inject} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {NavbarComponent} from '@components/navbar/navbar.component';
import {FooterComponent} from '@components/footer/footer.component';
import {ToastContainerComponent} from '@/app/shared/components/toast-container/toast-container.component';
import {TranslateService} from '@ngx-translate/core';
import defaultLanguage from	'../../public/assets/i18n/fr.json';
import {CookieModalComponent} from '@components/cookie-modal/cookie-modal.component';
import {AnalyticsService} from '@services/analytics.service';
import {AuthService} from '@services/auth.service';
import {BellComponent} from '@components/bell/bell.component';
import {HeadbandTwitchComponent} from '@components/headband-twitch/headband-twitch.component';
import {EventCalendar} from '@/app/core/models/calendar/eventCalendar';
import {MatchService} from '@services/match.service';
import {TwitchIframeComponent} from '@components/twitch-iframe/twitch-iframe.component';
import {FeatureFlagDirective} from '@/app/shared/directives/feature-flag.directive';
import {LoaderComponent} from '@/app/shared/components/loader/loader.component';
import {LoadingService} from '@services/loading.service';
import {SeoService} from '@/app/core/seo/seo.service';
import {JsonLdService} from '@/app/core/seo/json-ld.service';
import {buildOrganizationJsonLd} from '@/app/core/seo/organization.jsonld';

@Component({
	selector: 'app-root',
	imports: [SeasonalSnowComponent, RouterOutlet, NavbarComponent, FooterComponent, ToastContainerComponent, CookieModalComponent, BellComponent, HeadbandTwitchComponent, TwitchIframeComponent, FeatureFlagDirective, LoaderComponent],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {

	/** true uniquement dans le navigateur : garde-fou pour tous les accès DOM / localStorage (SSR). */
	private readonly isBrowser: boolean;

	private readonly seo = inject(SeoService);
	private readonly seasonalTheme = inject(SeasonalThemeService);
	private readonly jsonLd = inject(JsonLdService);
	private readonly destroyRef = inject(DestroyRef);
	protected readonly loading = inject(LoadingService);

	currentEventCalendar: EventCalendar | null = null;
	showTwitchBanner: boolean = false;
	isPlaceRoute: boolean = false;

	constructor(
		private readonly translate: TranslateService,
		private readonly analyticsService: AnalyticsService,
		protected readonly authService: AuthService,
		private readonly matchService: MatchService,
		private readonly router: Router,
		@Inject(PLATFORM_ID) private readonly platformId: object,
	) {
		this.seasonalTheme.init();

		this.translate.setTranslation('fr', defaultLanguage);
		this.translate.addLangs(['fr', 'en']);
		this.translate.setDefaultLang('en');

		this.isBrowser = isPlatformBrowser(this.platformId);
		const storedLang = this.isBrowser ? localStorage.getItem('lang') : null;

		// Pas de détection automatique de `navigator.language` : Googlebot exécute
		// le JS dans un Chrome headless dont la langue par défaut est souvent en-US,
		// ce qui basculait le rendu en anglais au moment précis où Google capture
		// le DOM pour l'indexation — malgré un SSR servi en français. Le français
		// reste donc la langue par défaut tant que l'utilisateur n'a pas choisi
		// explicitement l'anglais via le sélecteur de langue (navbar).
		const langToUse = storedLang ?? 'fr';

		this.translate.use(langToUse);
		if (this.isBrowser) localStorage.setItem('lang', langToUse);

		this.analyticsService.init();
	}

	ngOnInit(): void {
		// Balises des pages qui déclarent `data.seo` sur leur route.
		// Les pages de détail posent les leurs depuis les données chargées.
		this.seo.watchRouterData(this.destroyRef);
		// Identité du club : présente sur toutes les pages, jamais retirée.
		this.jsonLd.set('organization', buildOrganizationJsonLd());

		if (this.authService.isAuthenticated()) {
			this.authService.loadUser().subscribe();
		}

		this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				this.isPlaceRoute = this.router.url === '/place';
			}
		});

		if (this.router.url === '/') {
			// La requête part aussi du serveur : sa réponse est sérialisée dans le
			// transfer state, ce qui évite au navigateur de la refaire à l'hydratation.
			this.matchService.getCurrentEventCalendar().subscribe({
				next: (eventCalendar) => {
					this.currentEventCalendar = eventCalendar;

					// Seule la décision d'affichage dépend de localStorage, absent
					// côté serveur : le bandeau reste masqué au rendu SSR.
					if (!this.isBrowser) return;

					const closedAt = localStorage.getItem('twitchBannerClosedAt');
					const today = new Date().toDateString();

					if (this.currentEventCalendar?.streamUrl?.includes('fefegg') && closedAt !== today) {
						this.showTwitchBanner = true;
					} else {
						this.showTwitchBanner = false;
					}
				},
				error: (error) => {
					console.error('Error fetching current event:', error);
				}
			});
		}
	}

	closeTwitchBanner(): void {
		this.showTwitchBanner = false;
		const today = new Date().toDateString();
		localStorage.setItem('twitchBannerClosedAt', today);
	}
}
